import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  Lock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Search,
  AlertCircle
} from 'lucide-react';

export default function ProjectClosure() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCheckDialog, setShowCheckDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('__all__');

  const [closeForm, setCloseForm] = useState({
    project_id: '',
    closure_type: 'TECO',
    closure_date: new Date().toISOString().split('T')[0],
    reason: '',
    force_closure: false
  });


  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await psAPI.getProjects(filterStatus !== '__all__' ? { status: filterStatus } : {});
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCheckClosure = async (project) => {
    setSelectedProject(project);
    setCheckLoading(true);
    setShowCheckDialog(true);
    
    try {
      const response = await psAPI.checkProjectClosure({
        project_id: project.project_id,
        check_open_items: true,
        check_commitments: true,
        check_settlement: true,
        check_budget_carryforward: true
      });
      setCheckResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check project');
      setShowCheckDialog(false);
    } finally {
      setCheckLoading(false);
    }
  };

  const handleOpenCloseDialog = (project) => {
    setSelectedProject(project);
    setCloseForm({
      project_id: project.project_id,
      closure_type: 'TECO',
      closure_date: new Date().toISOString().split('T')[0],
      reason: '',
      force_closure: false
    });
    setShowCloseDialog(true);
  };

  const handleCloseProject = async () => {
    try {
      const response = await psAPI.closeProject(closeForm);
      toast.success(`Project ${response.data.new_status} successfully`);
      setShowCloseDialog(false);
      setShowCheckDialog(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close project');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      CREATED: 'bg-gray-100 text-gray-700',
      RELEASED: 'bg-emerald-100 text-emerald-700',
      ACTIVE: 'bg-orange-100 text-orange-700',
      TECO: 'bg-amber-100 text-amber-700',
      CLOSED: 'bg-orange-100 text-orange-700',
      ARCHIVED: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
  };

  const getCheckIcon = (check) => {
    if (check.passed) {
      return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    }
    if (check.severity === 'ERROR') {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }
    return <AlertTriangle className="w-5 h-5 text-amber-500" />;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Closure</h1>
            <p className="text-gray-500">Check and close projects</p>
          </div>
          <Button onClick={fetchProjects} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filter */}
        <div className="flex gap-4">
          <div className="w-64">
            <Label>Filter by Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Status</SelectItem>
                <SelectItem value="CREATED">Created</SelectItem>
                <SelectItem value="RELEASED">Released</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="TECO">Technically Complete</SelectItem>
                <SelectItem value="CLOSED">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Projects Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-orange-600" />
              Projects ({projects.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-orange-600" />
              </div>
            ) : projects.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3">Project ID</th>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Start Date</th>
                      <th className="text-left p-3">End Date</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.project_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium text-orange-600">{project.project_id}</td>
                        <td className="p-3">{project.name}</td>
                        <td className="p-3">{project.project_type}</td>
                        <td className="p-3">{getStatusBadge(project.status)}</td>
                        <td className="p-3">{project.start_date}</td>
                        <td className="p-3">{project.end_date}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleCheckClosure(project)}
                              data-testid={`check-${project.project_id}`}
                            >
                              <Search className="w-3 h-3 mr-1" />
                              Check
                            </Button>
                            {!['CLOSED', 'ARCHIVED'].includes(project.status) && (
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => handleOpenCloseDialog(project)}
                                data-testid={`close-${project.project_id}`}
                              >
                                <Lock className="w-3 h-3 mr-1" />
                                Close
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Lock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No projects found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Check Results Dialog */}
      <Dialog open={showCheckDialog} onOpenChange={setShowCheckDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Closure Check: {selectedProject?.name}</DialogTitle>
          </DialogHeader>
          {checkLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-orange-600" />
            </div>
          ) : checkResult && (
            <div className="space-y-4 py-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg ${checkResult.ready_for_teco ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  <div className="flex items-center gap-2">
                    {checkResult.ready_for_teco ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    )}
                    <span className={checkResult.ready_for_teco ? 'text-emerald-700' : 'text-amber-700'}>
                      {checkResult.ready_for_teco ? 'Ready for TECO' : 'Not ready for TECO'}
                    </span>
                  </div>
                </div>
                <div className={`p-4 rounded-lg ${checkResult.ready_for_closure ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  <div className="flex items-center gap-2">
                    {checkResult.ready_for_closure ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    )}
                    <span className={checkResult.ready_for_closure ? 'text-emerald-700' : 'text-amber-700'}>
                      {checkResult.ready_for_closure ? 'Ready for Closure' : 'Not ready for Closure'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{checkResult.errors}</p>
                  <p className="text-sm text-gray-500">Errors</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{checkResult.warnings}</p>
                  <p className="text-sm text-gray-500">Warnings</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    ${(checkResult.unsettled_costs || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">Unsettled</p>
                </div>
              </div>

              {/* Check Details */}
              <div className="space-y-2">
                <Label>Check Results</Label>
                <div className="space-y-2">
                  {checkResult.checks?.map((check, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        check.passed ? 'bg-emerald-50' : 
                        check.severity === 'ERROR' ? 'bg-red-50' : 'bg-amber-50'
                      }`}
                    >
                      {getCheckIcon(check)}
                      <div>
                        <p className="font-medium">{check.check_name}</p>
                        <p className="text-sm text-gray-600">{check.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckDialog(false)}>Close</Button>
            {checkResult && (checkResult.ready_for_teco || checkResult.ready_for_closure) && (
              <Button onClick={() => { setShowCheckDialog(false); handleOpenCloseDialog(selectedProject); }}>
                Proceed to Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Project Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Close Project: {selectedProject?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Closure Type *</Label>
              <Select value={closeForm.closure_type} onValueChange={(v) => setCloseForm({ ...closeForm, closure_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TECO">Technical Completion (TECO)</SelectItem>
                  <SelectItem value="CLOSE">Business Close</SelectItem>
                  <SelectItem value="ARCHIVE">Archive</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {closeForm.closure_type === 'TECO' && 'TECO: No more activities allowed, costs can still be posted'}
                {closeForm.closure_type === 'CLOSE' && 'Close: No more postings allowed'}
                {closeForm.closure_type === 'ARCHIVE' && 'Archive: Project moved to archive'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Closure Date</Label>
              <Input
                type="date"
                value={closeForm.closure_date}
                onChange={(e) => setCloseForm({ ...closeForm, closure_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Input
                value={closeForm.reason}
                onChange={(e) => setCloseForm({ ...closeForm, reason: e.target.value })}
                placeholder="Reason for closure"
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
              <input
                type="checkbox"
                checked={closeForm.force_closure}
                onChange={(e) => setCloseForm({ ...closeForm, force_closure: e.target.checked })}
                id="force-closure"
              />
              <label htmlFor="force-closure" className="text-sm text-amber-700">
                Force closure (ignore warnings)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>Cancel</Button>
            <Button onClick={handleCloseProject} data-testid="submit-close">
              Close Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
